import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import { IDataMotivo, IMotivo } from '@/shared/interfaces/erp/inventario/motivo.interface';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchMotivos = (params: GetMotivoParams) => {
  return useQuery({
    queryKey: ['motivo', ...Object.values(params || {})],
    queryFn: async () => {
      return getMotivos(params);
    },
  });
};

export const useGetMotivo = (id: string) => {
  return useQuery({
    queryKey: ['motivo', id],
    queryFn: async () => {
      return getMotivo(id);
    },
  });
};

export const useCreateMotivo = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createMotivo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['motivo'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Motivos ingresos creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateMotivo = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateMotivo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['motivo'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Motivos ingresos actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export interface GetMotivoParams extends Partial<IDataMotivo> {
  page?: number
  page_size?: number
  nombre?: string
  modulo_in?: string
};

export type CreateMotivoParams = Omit<IDataMotivo, 'id_motivo'>;
export type UpdateMotivoParams = {
  id: string;
  data: CreateMotivoParams;
};

export const getMotivos = async (params?: GetMotivoParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<IMotivo>(
    `${urlBackend}api/v1/motivo/?${queryParams}`,
    true
  );
};

export const getMotivo = async (id: string) => {
  try {
    return await get<IDataMotivo>(`${urlBackend}api/v1/motivo/${id}/`, true);
  }
  catch (error) {
    return handleAxiosError(error);
  }
};

export const createMotivo = async (data: CreateMotivoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return post<IDataMotivo>(`${urlBackend}api/v1/motivo/`, data, true);

};

export const updateMotivo = async ({ id, data }: UpdateMotivoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<IDataMotivo>(`${urlBackend}api/v1/motivo/${id}/`, data, true);
};
